import hash from "@emotion/hash"
import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { motion, useInView } from "framer-motion"
import { forwardRef, useMemo, useRef } from "react"

import { slideUp } from "animations/slideUp"

import { useConsole } from "contexts/Console"

import { contrastMode } from "css/a11y"
import getMediaQuery from "css/breakpoints"
import { fullGrid } from "css/grid"

import useParallax from "hooks/useParallax"

import generateModularBlocks from "utils/generateModularBlocks"

import FigCaption, { fullWidthInnerStyle, isInnerCN } from "components/figcaption/FigCaption"
import Image from "components/media/ImageCLD"

import { getGradient, modularTexts } from "./common"

const parallaxCN = `plx${hash("parallax")}`

const gradientCom = css`
  content: "";
  display: block;
  height: 20vh;
  position: absolute;
  width: 100%;
  left: 0;
  z-index: 0;
`

const MotionTag = forwardRef(({ isHeader = false, ...props }, ref) => {
  const Component = isHeader ? motion.header : motion.article
  return <Component ref={ref} {...props} />
})

const Root = styled(MotionTag)`
  &.inner-padding-s {
    --inner-pad: 100vw;
  }
  &.inner-padding-m {
    --inner-pad: 120vw;
  }
  &.inner-padding-l {
    --inner-pad: 140vw;
  }

  ${getMediaQuery("m")} {
    &.inner-padding-s {
      --inner-pad: 40vw;
    }
    &.inner-padding-m {
      --inner-pad: 50vw;
    }
    &.inner-padding-l {
      --inner-pad: 70vw;
    }
  }

  ${fullGrid}
  grid-template-rows: [article-start] min-height [article-end];
  position: relative;
  overflow: hidden;

  &.transitopbg::before {
    ${gradientCom}
    z-index: 1;
    top: 0;
    background: rgb(var(--top-color));
    -webkit-mask-image: linear-gradient(black, transparent);
  }
  &.transibotbg::after {
    ${gradientCom}
    bottom: 0;
    background: rgb(var(--bot-color));
    -webkit-mask-image: linear-gradient(transparent, black);
  }

  &.${parallaxCN} {
    picture {
      img {
        transform: translateY(var(--ypos)) scale(1.2);
      }
    }
  }
`

const TextContainer = styled(motion.div)`
  position: relative;
  grid-row: article;
  align-self: end;
  /* color: inherit; */
  text-align: start;
  grid-column: main;
  z-index: 1;
  margin-block: 20vw;

  ${getMediaQuery("m")} {
    margin-block: 10vw;

    .h-align-start & {
      grid-column: col 1 / span 6;

      [dir="rtl"] & {
        grid-column: col 7 / span 6;
      }
    }

    .h-align-center & {
      grid-column: col 3 / span 8;
    }

    .h-align-end & {
      grid-column: col 7 / span 6;

      [dir="rtl"] & {
        grid-column: col 1 / span 6;
      }
    }
  }

  ${getMediaQuery("l")} {
    margin-block: 10vw;

    .h-align-start & {
      grid-column: col 1 / span 5;

      [dir="rtl"] & {
        grid-column: col 8 / span 5;
      }
    }

    .h-align-center & {
      grid-column: col 4 / span 6;
    }

    .h-align-end & {
      grid-column: col 8 / span 5;

      [dir="rtl"] & {
        grid-column: col 1 / span 5;
      }
    }
  }

  .v-align-start & {
    margin-block-end: var(--inner-pad);
  }
  .v-align-end & {
    margin-block-start: var(--inner-pad);
  }
  .v-align-center & {
    margin-block: var(--inner-pad);
  }

  ${modularTexts}
  ${contrastMode}

  &.without-margin hgroup:not(:last-child),&.without-margin h1:not(:last-child), &.without-margin h2:not(:last-child) {
    margin-block-end: 0;
  }
`

const Figure = styled.figure`
  position: absolute;
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .v-align-start & img {
    object-position: bottom;
  }
  .v-align-end & img {
    object-position: top;
  }
  .v-align-center & img {
    object-position: center;
  }

  ${fullGrid};

  & picture,
  & img {
    grid-column: doc;
    grid-row: 1;
    overflow: hidden;
  }

  & figcaption.${isInnerCN} {
    ${fullWidthInnerStyle};
  }
`

export default function ArticleVarHeight({
  className,
  vertical_align,
  horizontal_align,
  background_image_cld: background_image,
  texts,
  inner_padding,
  transition_gradient,
  title_gradient,
  chapo_gradient,
  mosaic_scroll,
  animation,
  fig_caption,
  theme,
  cover = false,
}) {
  const console = useConsole()

  const modularBlocks = useMemo(
    () =>
      generateModularBlocks(texts, {
        console,
        inject: cover ? { BigHeading: { tag: "h1" }, Heading: { tag: "h1" } } : {},
      }),
    [cover, texts, console]
  )
  const prefersReducedMotion = process.browser && document.documentElement.classList.contains("prefers-reduced-motion")
  const ttlGradient = title_gradient?.colors ? getGradient(title_gradient.colors) : null
  const chapoGradient = chapo_gradient?.colors ? getGradient(chapo_gradient.colors) : null
  const transiGrad = `${transition_gradient.top_color ? "transitopbg " : ""}${transition_gradient.bottom_color ? "transibotbg " : ""}`
  const textContRef = useRef()
  const rootRef = useRef()
  const isTextInView = useInView(textContRef, { margin: "100% 0% 0% 0%" })
  const y = useParallax(rootRef)

  const heading = texts[0]?.heading
  const chapo = texts[1]?.chapo?.text
  const withoutMargin = !!heading?.title && !!chapo ? "without-margin" : ""

  return (
    <Root
      ref={rootRef}
      isHeader={cover}
      style={{
        "--top-color": transition_gradient.top_color,
        "--bot-color": transition_gradient.bottom_color,
        "--title-gradient": ttlGradient,
        "--chapo-gradient": chapoGradient,
        "--ypos": y,
      }}
      className={`${transiGrad} v-align-${vertical_align?.align} h-align-${horizontal_align?.align} inner-padding-${inner_padding} ${
        animation === "parallax" ? parallaxCN : ""
      } ${className}`}
    >
      <Figure>
        <Image {...background_image} sizes='100vw' />
        {fig_caption?.caption?.length ? <FigCaption {...fig_caption} /> : null}
      </Figure>
      <TextContainer
        className={`${mosaic_scroll ? "is-mosaic-scroll " : ""}${withoutMargin} ${theme?.theme || ""}`}
        ref={textContRef}
        variants={slideUp}
        initial='hidden'
        animate={prefersReducedMotion ? "show" : isTextInView ? "show" : "hide"}
      >
        {modularBlocks}
      </TextContainer>
    </Root>
  )
}
